import React, { useState, useEffect } from 'react';
import { Property } from '../../types';
import { XMarkIcon, SaveIcon, SpinnerIcon } from '../../components/Icons';
import { CIEN_CUADRAS_BUTTON_BLUE } from '../../constants';
import { fetchInmobiliariasConfigApi } from '../../services/configApiClient';
import { PropertyGeneralFields } from './property_edit/PropertyGeneralFields';
import { PropertyPricingFields } from './property_edit/PropertyPricingFields';
import { PropertySpecsFields } from './property_edit/PropertySpecsFields';
import { PropertyMediaFeaturesFields } from './property_edit/PropertyMediaFeaturesFields';

interface EditPropertyModalProps {
  property: Property | null;
  isOpen: boolean;
  onSave: (updatedData: Partial<Property>) => Promise<void>;
  onClose: () => void;
}

export const EditPropertyModal: React.FC<EditPropertyModalProps> = ({ property, isOpen, onSave, onClose }) => {
  const [formData, setFormData] = useState<Partial<Property>>({});
  const [activeTab, setActiveTab] = useState<'general' | 'pricing' | 'specs' | 'media'>('general');
  const [availableOwners, setAvailableOwners] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (property && isOpen) {
      setFormData({
        ...property,
        neighborhoodName: property.neighborhoodName || (property as any).barrio || '',
        codigo_brp: property.codigo_brp || (property as any).codigo_brp_o_inmobiliaria || '',
        nombre_cliente: property.nombre_cliente || (property as any).contacto_nombre || (property as any).nombre_contacto || '',
        telefono_cliente: property.telefono_cliente || (property as any).contacto_telefono || (property as any).celular_contacto || '',
        correo_1: property.correo_1 || (property as any).correo_contacto || '',
        images: property.images || (property as any).imagenes || [],
        video_youtube_url: property.video_youtube_url || (property as any).link_recorrido_virtual || '',
        comision_override_broker: property.comision_override_broker ?? (property as any).brokerCommissionRate ?? null,
        comision_override_premium: property.comision_override_premium ?? (property as any).premiumCommissionRate ?? null,
        comision_override_interno: property.comision_override_interno ?? (property as any).commissionInterno ?? null,
        tabla_comisiones: property.tabla_comisiones || (property as any).commissionTiers || null,
        ascensor: property.ascensor !== undefined && property.ascensor !== null ? Number(property.ascensor) : ((property as any).num_ascensores != null && (property as any).num_ascensores !== '' ? Number((property as any).num_ascensores) : null),
      });
      setActiveTab('general');
      setErrorMsg(null);
      fetchInmobiliariasConfigApi().then((data) => {
        const ownersSet = new Set<string>();
        Object.values(data).forEach((d: any) => {
          if (d && typeof d === 'object') {
            Object.keys(d).forEach((k) => k !== 'id' && typeof d[k] === 'object' && ownersSet.add(k));
          }
        });
        setAvailableOwners(Array.from(ownersSet).sort());
      }).catch(console.error);
    }
  }, [property, isOpen]);

  if (!isOpen || !property) return null;

  const handleChange = (field: keyof Property, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setErrorMsg(null);
    try {
      await onSave(formData);
      onClose();
    } catch (err: any) {
      setErrorMsg(err?.message || 'Error al guardar cambios');
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = [
    { id: 'general', label: '1. Información General' },
    { id: 'pricing', label: '2. Precios & Comisiones' },
    { id: 'specs', label: '3. Medidas & Características' },
    { id: 'media', label: '4. Multimedia & Descripción' },
  ] as const;

  return (
    <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm z-[200] flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden animate-fade-in-up">
        <header className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
          <div>
            <h3 className="text-base sm:text-lg font-black text-gray-800">
              Editar Inmueble: <span className="text-sky-600 font-mono">{formData.codigo || property.id}</span>
            </h3>
            <p className="text-xs text-gray-500">Edición completa de todos los campos en base de datos.</p>
          </div>
          <button onClick={onClose} disabled={isSaving} className="text-gray-400 hover:text-gray-700 p-1 rounded-full">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </header>

        <div className="flex border-b border-gray-200 bg-gray-50/50 px-6 gap-2 overflow-x-auto text-xs font-bold">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-3 border-b-2 whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'border-sky-600 text-sky-600'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {errorMsg && (
          <div className="mx-6 mt-3 p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs font-medium">
            {errorMsg}
          </div>
        )}

        <form onSubmit={handleSave} className="flex-1 overflow-y-auto p-6 flex flex-col justify-between">
          <div className="mb-6">
            {activeTab === 'general' && (
              <PropertyGeneralFields formData={formData} onChange={handleChange} availableOwners={availableOwners} />
            )}
            {activeTab === 'pricing' && <PropertyPricingFields formData={formData} onChange={handleChange} />}
            {activeTab === 'specs' && <PropertySpecsFields formData={formData} onChange={handleChange} />}
            {activeTab === 'media' && <PropertyMediaFeaturesFields formData={formData} onChange={handleChange} />}
          </div>

          <div className="pt-4 border-t border-gray-100 flex justify-end gap-3">
            <button type="button" onClick={onClose} disabled={isSaving} className="px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-100 rounded-lg">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-6 py-2 bg-[#0073b7] text-white rounded-lg text-xs font-black uppercase tracking-wider shadow-md hover:bg-sky-700 active:scale-95 transition-all flex items-center gap-2"
              style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}
            >
              {isSaving ? <SpinnerIcon className="w-4 h-4 animate-spin" /> : <SaveIcon className="w-4 h-4" />}
              {isSaving ? 'Guardando...' : 'Guardar Inmueble'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
